import React, { useState } from 'react';
import { getDocumentPreviewUrl, isImageFileUrl, isFileTooLargeForPreview, isOfficeFileUrl, isPdfFileUrl } from '../../utils/fileHelpers';

const getFileNameFromUrl = (fileUrl) => {
  if (!fileUrl) return 'Document';
  try {
    const decoded = decodeURIComponent(fileUrl);
    const cleanUrl = decoded.split(/[?#]/)[0];
    const parts = cleanUrl.split('/');
    const lastPart = parts[parts.length - 1];
    return lastPart || 'Document';
  } catch (e) {
    const cleanUrl = fileUrl.split(/[?#]/)[0];
    const parts = cleanUrl.split('/');
    return parts[parts.length - 1] || 'Document';
  }
};

const getFileIconAndType = (fileUrl) => {
  if (/\.pdf(?:[?#].*)?$/i.test(fileUrl)) {
    return { icon: '📕', type: 'PDF Document', color: '#EF4444' };
  }
  if (/\.(docx?|rtf)(?:[?#].*)?$/i.test(fileUrl)) {
    return { icon: '📘', type: 'Word Document', color: '#3B82F6' };
  }
  if (/\.(xlsx?|csv)(?:[?#].*)?$/i.test(fileUrl)) {
    return { icon: '📗', type: 'Spreadsheet', color: '#10B981' };
  }
  if (/\.(pptx?)(?:[?#].*)?$/i.test(fileUrl)) {
    return { icon: '📙', type: 'Presentation', color: '#F59E0B' };
  }
  if (/\.(zip|rar|tar|gz|7z)(?:[?#].*)?$/i.test(fileUrl)) {
    return { icon: '📦', type: 'Archive', color: '#8B5CF6' };
  }
  return { icon: '📄', type: 'Document', color: '#6B7280' };
};

const FilePreview = ({
  url,
  previewUrl,
  title = 'File Preview',
  className,
  style,
  width = '100%',
  height = '100%',
  onLoad,
  onError,
  alt = 'Preview',
  showOpenInNewTab = true,
  fileSize = null,
  isPreviewUrl = false, // New prop to indicate if URL is already a preview URL
  showPreview = true,
  showIframe = false,
}) => {
  if (!url) return null;

  const isTooLarge = isPreviewUrl ? false : isFileTooLargeForPreview(url, fileSize);
  const actualPreviewUrl = (previewUrl && previewUrl !== url) ? previewUrl : null;
  const rawUrlToUse = actualPreviewUrl || url;
  const resolvedPreviewUrl = getDocumentPreviewUrl(rawUrlToUse);

  // Determine if it's compact layout early
  const isCompact = height && (
    (typeof height === 'number' && height <= 150) ||
    (typeof height === 'string' && parseInt(height) <= 150)
  );

  // Prevent auto-download of Office documents when bypassing standard wrappers
  const safePreviewUrl = isOfficeFileUrl(rawUrlToUse) && !resolvedPreviewUrl.includes('officeapps.live.com') && !resolvedPreviewUrl.includes('docs.google.com')
    ? getDocumentPreviewUrl(rawUrlToUse)
    : resolvedPreviewUrl;

  const openInNewTab = (event) => {
    event?.stopPropagation?.();
    const targetUrl = safePreviewUrl || getDocumentPreviewUrl(url);
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  };

  const downloadFile = (event) => {
    event?.stopPropagation?.();
    const targetUrl = safePreviewUrl || getDocumentPreviewUrl(url);
    const link = document.createElement('a');
    link.href = targetUrl;
    link.download = title || getFileNameFromUrl(url);
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const buttonWrapperStyle = {
    position: 'absolute',
    bottom: 8,
    left: 8,
    right: 8,
    zIndex: 1000,
    display: 'flex',
    gap: '4px',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    width: 'calc(100% - 16px)',
    boxSizing: 'border-box',
  };

  const buttonStyle = {
    border: 'none',
    borderRadius: 6,
    color: '#ffffff',
    fontSize: 12,
    fontWeight: 700,
    padding: '8px 12px',
    cursor: 'pointer',
    lineHeight: 1.2,
    minWidth: 0,
    width: '100%',
    whiteSpace: 'nowrap',
    textAlign: 'center',
    boxSizing: 'border-box',
  };

  const previewButtonStyle = {
    ...buttonStyle,
    background: 'rgba(13, 27, 42, 0.9)',
  };

  const downloadButtonStyle = {
    ...buttonStyle,
    background: 'rgba(34, 197, 94, 0.9)',
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    background: 'rgba(13, 27, 42, 0.1)',
    color: 'var(--color-oxford-blue, #0d1b2a)',
    border: '1px solid rgba(13, 27, 42, 0.2)',
  };

  if (isTooLarge) {
    return (
      <div style={{
        position: 'relative',
        width,
        height,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8f9fa',
        border: '2px dashed #dee2e6',
        borderRadius: '8px',
        padding: '20px',
        textAlign: 'center',
        ...style
      }}>
        <div style={{ fontSize: '48px', color: 'var(--color-turquoise-teal)', marginBottom: '10px' }}>
          📄
        </div>
        <div style={{ fontSize: '14px', color: 'var(--color-oxford-blue)', marginBottom: '15px' }}>
          File too large to preview
        </div>
        <div style={{ fontSize: '12px', color: 'var(--color-turquoise-teal)', marginBottom: '15px' }}>
          {fileSize ? `${(fileSize / (1024 * 1024)).toFixed(1)} MB` : 'Large file'}
        </div>
        <div style={buttonWrapperStyle}>
          <button type="button" onClick={downloadFile} style={downloadButtonStyle}>
            Download ↓
          </button>
        </div>
      </div>
    );
  }

  if (isImageFileUrl(url)) {
    return (
      <div style={{ position: 'relative', width, height }}>
        <img
          src={url}
          alt={alt}
          className={className}
          style={{ width: '100%', height: '100%', objectFit: 'contain', ...style }}
          onLoad={onLoad}
          onError={onError}
        />
        {showOpenInNewTab && (
          <div style={buttonWrapperStyle}>
            <button type="button" onClick={openInNewTab} style={previewButtonStyle}>
              Preview ↗
            </button>
            <button type="button" onClick={downloadFile} style={downloadButtonStyle}>
              Download ↓
            </button>
          </div>
        )}
      </div>
    );
  }

  const isLocalOrBlob = url.startsWith('blob:') || url.startsWith('data:') || url.includes('localhost') || url.includes('127.0.0.1');
  const isIframeRenderable = showIframe && !isCompact && (isPdfFileUrl(url) || (!isLocalOrBlob && (isOfficeFileUrl(url) || !isImageFileUrl(url))));
  const shouldShowIframe = showPreview && showIframe && isIframeRenderable;

  if (shouldShowIframe) {
    const displayHeight = height && height !== '100%' ? height : '220px';
    return (
      <div style={{ position: 'relative', width, height: displayHeight, ...style }}>
        <iframe
          src={safePreviewUrl}
          width="100%"
          height="100%"
          title={title}
          className={className}
          style={style}
          onLoad={onLoad}
          onError={onError}
        />
        <div style={buttonWrapperStyle}>
          <button type="button" onClick={openInNewTab} style={previewButtonStyle}>
            Preview ↗
          </button>
          <button type="button" onClick={downloadFile} style={downloadButtonStyle}>
            Download ↓
          </button>
        </div>
      </div>
    );
  }

  const fileInfo = getFileIconAndType(url);
  const fileName = getFileNameFromUrl(url);
  const displayTitle = title && title !== 'File Preview' && title !== 'Document Viewer' && !title.startsWith('Document Viewer') ? title : fileName;

  const cardStyle = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    background: 'var(--color-frosted-silver-sage, #f8f9fa)',
    border: '1px solid var(--color-border, #dee2e6)',
    borderRadius: '8px',
    padding: isCompact ? '6px' : '12px',
    boxSizing: 'border-box',
    width: '100%',
    height: height || '100%',
    minHeight: isCompact ? '70px' : '100px',
    position: 'relative',
    overflow: 'hidden',
    ...style,
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: isCompact ? '6px' : '10px',
    textAlign: 'left',
    width: '100%',
    overflow: 'hidden',
    paddingRight: '30px',
  };

  const iconContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: isCompact ? '24px' : '36px',
    height: isCompact ? '24px' : '36px',
    borderRadius: isCompact ? '6px' : '8px',
    backgroundColor: `${fileInfo.color}15`,
    fontSize: isCompact ? '14px' : '20px',
    flexShrink: 0,
  };

  const textContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    flexGrow: 1,
  };

  const fileNameStyle = {
    fontSize: isCompact ? '11px' : '13px',
    fontWeight: '600',
    color: 'var(--color-oxford-blue, #0d1b2a)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    margin: 0,
  };

  const fileMetaStyle = {
    fontSize: isCompact ? '9px' : '11px',
    color: 'var(--color-oxford-blue, #0d1b2a)',
    opacity: 0.7,
    marginTop: isCompact ? '1px' : '2px',
    margin: 0,
  };

  const actionsContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginTop: '10px',
    width: '100%',
    boxSizing: 'border-box',
  };

  return (
    <div style={cardStyle}>
      <div style={headerStyle}>
        <div style={iconContainerStyle}>
          {fileInfo.icon}
        </div>
        <div style={textContainerStyle}>
          <p style={fileNameStyle} title={displayTitle}>{displayTitle}</p>
          <p style={fileMetaStyle}>
            {fileInfo.type} {fileSize ? `• ${(fileSize / (1024 * 1024)).toFixed(1)} MB` : ''}
          </p>
        </div>
      </div>
      <div style={actionsContainerStyle}>
        <button type="button" onClick={openInNewTab} style={previewButtonStyle}>
          Preview ↗
        </button>
        <button type="button" onClick={downloadFile} style={downloadButtonStyle}>
          Download ↓
        </button>
      </div>
    </div>
  );
};

export default FilePreview;